import React, { useState, useCallback } from 'react';
import { formatDate } from '@/utils';
import { Button } from '@/components/ui/Button';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';
import { ResponseCommentData } from '@/hooks/useResponses';

interface ResponseCommentsSectionProps {
  comments: ResponseCommentData[];
  canComment: boolean;
  currentUserId?: string;
  onAddComment: (content: string, stageName?: string) => void;
  onUpdateComment?: (commentId: string, content: string) => void;
  onDeleteComment?: (commentId: string) => void;
  currentStage?: string;
  isLoading?: boolean;
  isAdding?: boolean;
}

export const ResponseCommentsSection: React.FC<ResponseCommentsSectionProps> = ({
  comments,
  canComment,
  currentUserId,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  currentStage,
  isLoading = false,
  isAdding = false
}) => {
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const { confirm } = useConfirmation();

  const handleSubmit = () => {
    if (newComment.trim()) {
      onAddComment(newComment.trim(), currentStage);
      setNewComment('');
    }
  };

  const handleEdit = (comment: ResponseCommentData) => {
    setEditingCommentId(comment._id);
    setEditingContent(comment.content);
  };

  const handleSaveEdit = (commentId: string) => {
    if (editingContent.trim() && onUpdateComment) {
      onUpdateComment(commentId, editingContent.trim());
      setEditingCommentId(null);
      setEditingContent('');
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };

  const handleDelete = useCallback(async (commentId: string) => {
    if (!onDeleteComment) return;
    const confirmed = await confirm({
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      onDeleteComment(commentId);
    }
  }, [onDeleteComment, confirm]);

  const canEditComment = (comment: ResponseCommentData) => {
    return comment.userId?._id === currentUserId;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Comments ({comments.length})
        </h3>
      </div>

      {/* Comment Input */}
      {canComment && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
            disabled={isAdding}
          />
          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {currentStage && <span>Comment will be added to: {currentStage}</span>}
            </div>
            <Button
              onClick={handleSubmit}
              disabled={!newComment.trim() || isAdding}
              size="sm"
            >
              {isAdding ? 'Adding...' : 'Add Comment'}
            </Button>
          </div>
        </div>
      )}

      {/* Comments List */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
            <p className="mt-2">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p className="mt-2">No comments yet</p>
            {canComment && <p className="text-sm mt-1">Be the first to comment!</p>}
          </div>
        ) : (
          comments.map((comment) => {
            const isEditing = editingCommentId === comment._id;
            const isOwnComment = canEditComment(comment);

            return (
              <div key={comment._id} className="p-4">
                {/* Comment Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                          {comment.userId?.firstName?.[0]}
                          {comment.userId?.lastName?.[0]}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {comment.userId?.firstName} {comment.userId?.lastName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(comment.createdAt)}
                        {comment.stageName && (
                          <span className="ml-2">
                            • <span className="font-medium">{comment.stageName}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {isOwnComment && !isEditing && (
                    <div className="flex items-center space-x-1">
                      {onUpdateComment && (
                        <button
                          onClick={() => handleEdit(comment)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                          title="Edit comment"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                      )}
                      {onDeleteComment && (
                        <button
                          onClick={() => handleDelete(comment._id)}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                          title="Delete comment"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Comment Content */}
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
                      autoFocus
                    />
                    <div className="flex items-center space-x-2">
                      <Button
                        onClick={() => handleSaveEdit(comment._id)}
                        disabled={!editingContent.trim()}
                        size="sm"
                      >
                        Save
                      </Button>
                      <Button onClick={handleCancelEdit} variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {comment.content}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
